import { useEffect, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { PROSPECTS_ROUTE } from '../../../../../constants/auth';
import { useAuth } from '../../../../../hooks/useAuth/useAuth';
import { useUser } from '../../../../../hooks/useUser/useUser';
import { TRANSLATIONS_GLOBAL } from '../../../../../translations/commons';
import DropdownMenu from '../../../DropdownMenu/DropdownMenu';

import './BrokerSelector.scss';

interface IBrokerSelectorProps {
  collapsed: boolean;
}

const BrokerSelector = ({ collapsed }: IBrokerSelectorProps) => {
  const { isBetterflyer } = useAuth();
  const { brokers, brokerId, setSelectedBrokerId } = useUser();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();

  useEffect(() => {
    if (!open) return;
    const handleOutsideClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [open]);

  if (!isBetterflyer || brokers.length === 0) return null;

  const selectedBroker = brokers.find((broker) => broker.brokerId === brokerId) ?? brokers[0];
  const brokerName = (broker: (typeof brokers)[number]) => broker.name ?? broker.brokerId;

  const handleSelect = (id: string) => {
    setOpen(false);
    if (id === brokerId) return;
    setSelectedBrokerId(id);
    navigate(PROSPECTS_ROUTE);
  };

  const menuItems = brokers.map((broker) => ({
    key: broker.brokerId,
    label: (
      <span className="broker-selector__option">
        <span className="broker-selector__name">{brokerName(broker)}</span>
        <span className="broker-selector__id">{broker.brokerId}</span>
      </span>
    ),
    active: broker.brokerId === brokerId,
    onClick: () => handleSelect(broker.brokerId)
  }));

  return (
    <div className="broker-selector" ref={containerRef}>
      <button
        type="button"
        className="broker-selector__trigger"
        aria-label={t(TRANSLATIONS_GLOBAL.brokerSelector.ariaLabel)}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
      >
        {collapsed ? (
          <span className="broker-selector__initial">{brokerName(selectedBroker).charAt(0).toUpperCase()}</span>
        ) : (
          <span className="broker-selector__info">
            <span className="broker-selector__label">{t(TRANSLATIONS_GLOBAL.brokerSelector.label)}</span>
            <span className="broker-selector__name">{brokerName(selectedBroker)}</span>
            <span className="broker-selector__id">{selectedBroker.brokerId}</span>
          </span>
        )}
      </button>
      {open && <DropdownMenu items={menuItems} placement="top" />}
    </div>
  );
};

export default BrokerSelector;
